import { useEffect } from 'react';
import { filterNullish } from '@modernhealth/filter-nullish';
import { DeprecatedInlineMessage } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import { useGetCircleRsvpsQuery } from '@modernhealth/circles-graphql/v2';

import { captureException } from 'src/shared/helpers/captureException';

import { CircleGrid, CirclePageSection } from './CirclePageSection';
import { SessionCardRowSkeleton } from './SessionCardSkeletons';
import { CircleRsvpCard } from './CircleRsvpCard';

const Container: React.FC<{ loading?: boolean }> = ({ children, loading }) => {
    const { t } = useTranslation('circles');

    return (
        <CirclePageSection
            header={t('sections.your_circles.header')}
            cta={!loading ? t('sections.your_circles.cta') : undefined}
            to="/circles/registered"
        >
            {children}
        </CirclePageSection>
    );
};

export const YourCirclesRowSkeleton = () => {
    return (
        <Container loading>
            <SessionCardRowSkeleton />
        </Container>
    );
};

export const YourCirclesRow = () => {
    const { t } = useTranslation('circles');

    const { data, loading, error } = useGetCircleRsvpsQuery({
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (error) captureException(error);
    }, [error]);

    if (loading && !data?.circleRsvps) {
        return <YourCirclesRowSkeleton />;
    }

    if (error || !data?.circleRsvps) return null;

    const { circleRsvps } = data;

    if (circleRsvps.__typename !== 'CircleRsvpCollection') return null;
    // Don't show if there are no RSVPs
    if (circleRsvps.edges.length === 0) {
        return (
            <Container>
                <DeprecatedInlineMessage
                    message={t('sections.your_circles.conditions.no_circles.message')}
                    action={{
                        to: '/circles/upcoming',
                        text: t('sections.your_circles.conditions.no_circles.action'),
                    }}
                    alignment="center"
                />
            </Container>
        );
    }

    return (
        <Container>
            <CircleGrid>
                {filterNullish(circleRsvps.edges)
                    .slice(0, 3)
                    .map(({ node }) => {
                        return (
                            <CircleRsvpCard key={node.id} rsvp={node} id={`rsvp-card-${node.id}`} />
                        );
                    })}
            </CircleGrid>
        </Container>
    );
};
